import dynamic from 'next/dynamic';
import {
  MouseEvent,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DOMAIN_PREFIX,
  LANGUAGE_ENUMS,
  LANGUAGE_VALUE,
  TLANGUAGE_VALUE,
  domainCode,
} from '../../../constants/staticPageConstants';
import { Meta } from '../../../layout/Meta';
import { DateFormatter } from '../../../utils/date-utils';
import { processURL } from '../../../utils/static-pages/urlProcessor';
import { handleTrack } from '../../../utils/tracker';
import { ButtonVariants } from '../../Atoms/Button';
import { If } from '../../Atoms/Conditionals';
import { FILE_NOW_TEXT } from '../../CTA/constants';
import {
  REQUEST_DEMO_LINK,
  demoText,
  showBookDemoPopUp,
} from '../../CTA/utils';
import { checkIfB2CPage } from '../../Header/utils';
import { CloseIcon } from '../../Icons';
import {
  AuthorDetails,
  CMSHeader,
  CMSLayoutSchema,
  H1Heading,
  NAV_TYPES,
  TopCTA,
} from './components';
import { INLINE_CTA, INLINE_CTA_CHECKLIST } from './constants';
import { useOnMountCMSLayout } from './hooks/useOnMountCMSLayout';
import { LANG_DATA } from './language/dataset';
import { LanguageSwitch } from './language/languageSwitch';
import { generateLanguageTags } from './language/languageTags';
import { BreadCrumbLayout, MainLayout, PageWrapper } from './styles';
import { IFAQ, ISINGLE_POST } from './types';
import {
  ReusableComponent,
  addLazyLoadAttributesToHtmlString,
  createTOCNewTemplateReact,
  decodeHtml,
  getActiveFAQsInFAQFormat,
  getBreadCrumbData,
  optimiseYoutube,
  stripOutsideRC,
  stripUnwantedStyles,
} from './util';

import { attachTrackers, removeTrackers } from '../utils/commonUtils';

interface ICMS_LAYOUT_PROPS {
  data: ISINGLE_POST;
  modifiedDate: string;
}

// Dynamic Imports with webpack magic comments for better code splitting
const MarktechModal = dynamic(
  () =>
    import(/* webpackChunkName: "modal-marktech" */ '../../Atoms/Modal').then(
      (mod) => mod.MarktechModal
    ),
  { suspense: true, ssr: false }
);
const DemoModal = dynamic(
  () =>
    import(/* webpackChunkName: "modal-demo" */ '../../Atoms/DemoModal').then(
      (mod) => mod.DemoModal
    ),
  { suspense: true, ssr: false }
);
const StickyCTA = dynamic(
  () =>
    import(/* webpackChunkName: "cta-sticky" */ '../../CTA/StickyCTA').then(
      (mod) => mod.StickyCTA
    ),
  { suspense: true }
);
const FaqComponent = dynamic(
  () =>
    import(/* webpackChunkName: "faq-component" */ '../../Common/FAQS').then(
      (mod) => mod.FaqComponent
    ),
  { suspense: true }
);
const AuthorCard = dynamic(
  () =>
    import(
      /* webpackChunkName: "author-card" */ '../../Author/AuthorCard'
    ).then((mod) => mod.AuthorCard),
  { suspense: true, ssr: true }
);
const MobileTocAI = dynamic(
  () =>
    import(/* webpackChunkName: "toc-mobile" */ './TOC/MobileTocAI').then(
      (mod) => mod.MobileTocAI
    ),
  { suspense: true, ssr: true }
);
const NewTableOfContent = dynamic(
  () =>
    import(/* webpackChunkName: "toc-desktop" */ './TOC/NewDesktopToc').then(
      (mod) => mod.NewTableOfContent
    ),
  { suspense: true, ssr: true }
);
const LeftSideArticlesAI = dynamic(
  () =>
    import(
      /* webpackChunkName: "articles-left-ai" */ './articles/leftArticleListAI'
    ).then((mod) => mod.LeftSideArticlesAI),
  { suspense: true, ssr: false }
);
const LeftSideArticles = dynamic(
  () =>
    import(
      /* webpackChunkName: "articles-left" */ './articles/leftArticleList'
    ).then((mod) => mod.LeftSideArticles),
  { suspense: true, ssr: false }
);
const KSA_LHS = dynamic(
  () =>
    import(
      /* webpackChunkName: "articles-ksa" */ './articles/leftArticleList'
    ).then((mod) => mod.KSA_LHS),
  { suspense: true, ssr: false }
);

const AIBreadcrumb = dynamic(
  () =>
    import(
      /* webpackChunkName: "breadcrumb-ai" */ '../../Atoms/BreadCrumb/AIBreadcrumb'
    ).then((mod) => mod.AIBreadcrumb),
  { suspense: true, ssr: true }
);

export const AICMSLayout = (params: ICMS_LAYOUT_PROPS): JSX.Element => {
  const { modifiedDate, data } = params;
  const [toc, setTOC] = useState<Array<{ id: string; content: string }>>([]);
  const [isMounted, setIsMounted] = useState(false);

  const [displayPopup, setDemoPopup] = useState(false);

  // Fix hydration: only show date after mount to avoid SSR/client mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const myContainer = useRef(null);
  const cms_content = useRef(null);
  const modalRefCTA = useRef(null);
  const isB2CPage = checkIfB2CPage(data.category.slug);
  const LANGUAGE = LANGUAGE_VALUE[
    data?.language as TLANGUAGE_VALUE
  ] as LANGUAGE_ENUMS;
  const pageURL = processURL(data.permalink, LANGUAGE, data.domain);

  let CURR_PAGE_URL = `https://www.${
    DOMAIN_PREFIX[data.domain]?.host
  }/${pageURL}`;

  if (data.domain === domainCode.cleartaxIn) {
    CURR_PAGE_URL = `https://${DOMAIN_PREFIX[data.domain]?.host}/${pageURL}`;
  }

  const authorDetails = data.author_details;
  const showAuthor =
    authorDetails.status === true && authorDetails.domain.includes(data.domain);

  // Process DOM after mount - use useEffect to avoid blocking main thread
  useEffect(() => {
    // Wait for isMounted to ensure client-side hydration is complete
    if (!isMounted || !myContainer.current) return;

    
    setTOC(createTOCNewTemplateReact(myContainer.current));
    
    stripOutsideRC(cms_content.current);
    if (data.faq) {
      data.faq?.forEach((item, index) => {
        if (data && data.faq && data.faq[index] && data.faq[index]?.answer) {
          data.faq[index] = {
            ...data.faq[index],
            answer: stripUnwantedStyles(item.answer),
          } as IFAQ;
        }
      });
    }

    // MOBILE-SPECIFIC OPTIMIZATION: Lazy load YouTube and Reusable Components
    // Only load when user scrolls OR after 5 seconds (whichever comes first)
    // Desktop loads immediately for better UX

    const isMobile = window.innerWidth <= 768; // Mobile breakpoint
    let hasLoaded = false;

    const loadNonCriticalContent = () => {
      if (hasLoaded) return;
      hasLoaded = true;

      // Load YouTube players
      try {
        if (myContainer.current) {
          optimiseYoutube(myContainer.current);
        }
      } catch (error) {
        console.error('Error optimizing YouTube players:', error);
      }

      // Load reusable components
      try {
        if (myContainer.current) {
          ReusableComponent(myContainer.current);
        }
      } catch (error) {
        console.error('Error loading reusable components:', error);
      }
    };

    if (isMobile) {
      // MOBILE: Lazy load on scroll or after 2 seconds

      const initialScrollY = window.scrollY || window.pageYOffset;

      // Track last scroll position to detect actual user scrolling
      let lastScrollY = initialScrollY;

      // Set up scroll listener with multiple event types for better compatibility
      const handleScroll = () => {
        const currentScroll = window.scrollY || window.pageYOffset;
        const scrollDelta = Math.abs(currentScroll - lastScrollY);

        // Only trigger if user actually scrolled (not just browser restoration)
        // Require 50px movement from last known position
        if (scrollDelta > 50) {
          loadNonCriticalContent();

          // Remove all listeners
          window.removeEventListener('scroll', handleScroll);
          window.removeEventListener('touchmove', handleScroll);
          document.removeEventListener('scroll', handleScroll);
        }

        lastScrollY = currentScroll;
      };

      // Attach to multiple events for mobile compatibility
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('touchmove', handleScroll, { passive: true });
      document.addEventListener('scroll', handleScroll, { passive: true });

      // Set up 2-second timeout as fallback (optimized for Lighthouse + real users)
      const timeoutId = setTimeout(() => {
        loadNonCriticalContent();

        // Remove all listeners
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('touchmove', handleScroll);
        document.removeEventListener('scroll', handleScroll);
      }, 2000);

      // Cleanup function
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('touchmove', handleScroll);
        document.removeEventListener('scroll', handleScroll);
        clearTimeout(timeoutId);
      };
    } else {
      // DESKTOP: Load immediately
      loadNonCriticalContent();
    }
  }, [isMounted]); // Run after isMounted is set to true

  const content_dom = cms_content.current;

  useEffect(() => {
    const trackers = attachTrackers(
      ['.rc-component', '.inline-cta-btn-text', '.cms-content a'],
      ['inline_cta', 'inline_cta', 'incontent_cta'],
      window.document
    );

    return () => {
      removeTrackers(trackers);
    };
  }, [content_dom]);

  useOnMountCMSLayout(data);

  // Process the HTML content string to add lazy loading attributes to images

  const BREADCRUMB_DATA = useMemo(
    () => getBreadCrumbData(data, LANGUAGE),
    [data, LANGUAGE]
  );
  const PAGE_CATEGORY =
    data.category.parent === null
      ? data.category.slug
      : data.category.parent.slug;
  const captureEvent = (e: MouseEvent<Element, MouseEvent>) => {
    if (!(e?.target instanceof HTMLElement)) {
      return;
    }
    const isLabelRequestADemo = e?.target.dataset.label;
    if (isLabelRequestADemo && isLabelRequestADemo === 'Request a demo') {
      e.preventDefault();
      setDemoPopup(true);
    }
    handleTrack({
      label: 'top_cta',
      obj: e.target,
    });
  };
  const stickyCTAProp = isB2CPage
    ? { type: NAV_TYPES.B2C }
    : { link: REQUEST_DEMO_LINK(data.domain) };

  const articalContent = useMemo(() => {
    const contentWithLazyImages = addLazyLoadAttributesToHtmlString(data.content);
    return contentWithLazyImages;
  }, [data.content]);
  return (
    <>
      <Suspense fallback={<div>Loading CTA...</div>}>
        <If condition={data.domain !== domainCode.cleartaxSA}>
          {showBookDemoPopUp(data.domain) ? (
            <div onClick={() => setDemoPopup(true)}>
              <StickyCTA
                buttonText={isB2CPage ? FILE_NOW_TEXT : demoText(data.domain)}
                buttonStyle={ButtonVariants.bluefilled}
                {...stickyCTAProp}
              />
            </div>
          ) : (
            <StickyCTA
              buttonText={isB2CPage ? FILE_NOW_TEXT : demoText(data.domain)}
              buttonStyle={ButtonVariants.bluefilled}
              {...stickyCTAProp}
            />
          )}
        </If>
      </Suspense>
      <Suspense fallback={<div>Loading Modal...</div>}>
        <MarktechModal
          open={displayPopup}
          onClose={() => setDemoPopup(false)}
          classNames={{
            modal:
              'md:w-11/12 sm:w-11/12 h-fit max-h-[90vh] overflow-hidden w-1/2 mobile-search-box rounded-2xl',
          }}
          closeIcon={<CloseIcon />}
          center
        >
          <DemoModal
            modalRef={modalRefCTA}
            demoPopup={setDemoPopup}
            language={LANGUAGE}
          />
        </MarktechModal>
      </Suspense>
      <CMSHeader
        domain={data.domain}
        category={data.category}
        language={LANGUAGE}
        isMultiLang={data.multi_language}
        permalink={data.permalink}
      />
      <Meta
        title={decodeHtml(data.meta_title).replace('&', 'and')}
        description={data.description}
        ampURL={
          pageURL === 'budget-2023-highlights' ? `${CURR_PAGE_URL}-amp` : null
        }
        canonical={CURR_PAGE_URL}
        favicon={`${
          data.domain === domainCode.ClearTech
            ? 'https://assets1.cleartax-cdn.com/cleartax/images/1675852730_image5.png'
            : 'https://assets1.cleartax-cdn.com/cleartax/images/1644906212_clearfavicon.png'
        } `}
        hrefLang={generateLanguageTags(data.multi_language, data)}
        isOgEnabled={true}
        img="https://assets.clear.in/cleartax/images/1753522640_cleartaxhomepage_.png"
      ></Meta>
      <PageWrapper theme={{ direction: data.language === 3 ? 'rtl' : 'ltr' }}>
        <div className="mobile-index sm:block hidden">
          <Suspense fallback={<div>Loading TOC...</div>}>
            <MobileTocAI tocIndexList={toc} dom={myContainer.current} />
          </Suspense>
        </div>
        <div className="sm:mt-12">
          <TopCTA CTA={data.cta || ''} onClickCB={captureEvent} />
        </div>
        <div
          className={`md:justify-between  ${
            data.permalink === 'income-tax-slabs' ? 'sm:mt-12' : ''
          } sm:block flex`}
          ref={myContainer}
          data-testid="cms-page"
        >
          <div className="cms-left-section w-1/5 sm:w-full sm:px-4 pr-8 pl-5 mt-4 md:px-4 font-medium sm:hidden mb-6">
            <Suspense fallback={<div>Loading TOC...</div>}>
              <NewTableOfContent
                language={LANGUAGE}
                tocIndexList={toc}
                dom={myContainer.current}
              />
            </Suspense>
          </div>
          <div className="w-[53%] md:w-[46%] lg:w-3/5 sm:w-full sm:px-5">
            <BreadCrumbLayout className="bg-white sm:pt-0 pt-4">
              <Suspense fallback={<div>Loading Breadcrumb...</div>}>
                <AIBreadcrumb
                  dataSet={BREADCRUMB_DATA}
                  url={data.permalink}
                  domain={data.domain}
                  language={LANGUAGE}
                />
              </Suspense>
            </BreadCrumbLayout>
            <H1Heading title={data.title} />
            <div className="flex mb-4  sm:flex-col sm:mb-2 items-center md:flex-wrap">
              <div className="flex w-full whitespace-pre sm:flex-col">
                <If condition={showAuthor}>
                  <AuthorDetails name={authorDetails.name} />
                </If>

                <div className="flex">
                  {isMounted && (
                    <>
                      <p className="text-s-12 py-2 sm:py-0 self-center sm:my-1 sm:text-s-10">
                        {LANG_DATA[LANGUAGE]?.captionItem.updatedOn}:{' '}
                        {DateFormatter(modifiedDate).format('MMM Do, YYYY')}
                      </p>
                      <p className="self-center"> | </p>
                      <p className="text-s-12 py-2  self-center w-full sm:py-0 sm:mx-0 sm:my-2 sm:w-full sm:text-s-10">
                        {Math.ceil(data.content.length / 5000)}{' '}
                        {LANG_DATA[LANGUAGE]?.captionItem.minRead}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <LanguageSwitch
                className="text-s-14 text-gray-500"
                isMultiLanguage={data.multi_language}
                pageData={data}
              />
            </div>

            <MainLayout>
              <div
                dangerouslySetInnerHTML={{
                  __html: articalContent,
                }}
                className="cms-content"
                ref={cms_content}
              />
              {INLINE_CTA_CHECKLIST.indexOf(data.category.id) > -1 && (
                <div
                  onClick={(e) => {
                    handleTrack({
                      label: 'inline_cta_faq',
                      obj: e.target,
                    });
                  }}
                  dangerouslySetInnerHTML={{ __html: INLINE_CTA }}
                />
              )}
              <Suspense fallback={<div>Loading FAQs...</div>}>
                <FaqComponent
                  FAQS={useMemo(
                    () => getActiveFAQsInFAQFormat(data.faq),
                    [data.faq]
                  )}
                  isShowSchema={true}
                  variant="partner"
                  faqIconColor={'#314259'}
                  htmlFromApi={true}
                  expanded={'false'}
                />
              </Suspense>
            </MainLayout>

            {isMounted && showAuthor && !!authorDetails?.description && (
              <div id="authorBio" className="pt-14">
                <Suspense fallback={<div>Loading Author...</div>}>
                  <AuthorCard
                    name={authorDetails.name}
                    designation={authorDetails.designation}
                    authorImg={authorDetails.image_url || ''}
                    description={authorDetails.description}
                    linkedin={authorDetails.linkedin_url}
                    facebook={authorDetails.facebook_url}
                    twitter={authorDetails.twitter_url}
                    slug={authorDetails.slug}
                    showHeading={true}
                  />
                </Suspense>
              </div>
            )}
          </div>
          <div className="px-7 sm:px-5 w-[28%] sm:w-full right-articles-section">
            <Suspense fallback={<div>Loading Articles...</div>}>
              {data.domain === domainCode.cleartaxIn ||
              data.domain === domainCode.ClearTech ? (
                <div className="">
                  <LeftSideArticlesAI
                    domain={data.domain}
                    pageLanguage={LANGUAGE}
                    permalink={data.permalink}
                    content_summary={data.content_summary}
                    pageType={data.content_type}
                    relatedPost={data.related_posts}
                    popularPost={data.popular_posts}
                    interestedPost={data.interesting_posts}
                    category={PAGE_CATEGORY}
                    isB2CPage={isB2CPage}
                  />
                </div>
              ) : (
                <>
                  <KSA_LHS domain={data.domain} language={data.language} />
                  <LeftSideArticles
                    relatedPost={data.related_posts}
                    popularPost={data.popular_posts}
                    interestedPost={data.interesting_posts}
                    language={data.language}
                    domainName={DOMAIN_PREFIX[data.domain]?.domain || ''}
                  />
                </>
              )}
            </Suspense>
          </div>
        </div>
      </PageWrapper>
      <CMSLayoutSchema
        postData={data}
        pageUrl={CURR_PAGE_URL}
        language={LANGUAGE}
      />
    </>
  );
};
